import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import { supabase } from '../supabase';
import './tablePage.css';
import searchIcon from '../assets/search.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const CALL_LOGS_PER_PAGE = 4;

const CallLogs = () => {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [userId, setUserId] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [id, setId] = useState('');
  const [editUserId, setEditUserId] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [isEditOpen, setIsEditOpen] = useState(false);

  const getLogs = async () => {
    const res = await supabase
      .from('call_logs')
      .select('id,created_at,user_id,message,status,users(name,email)')
      .order('id', { ascending: false });
    setLogs(res.data || []);
  };

  const getUsers = async () => {
    const res = await supabase.from('users').select('id,name,email').order('id');
    setUsers(res.data || []);
  };

  useEffect(() => {
    getLogs();
    getUsers();
  }, []);

  const createLog = async (e) => {
    e.preventDefault();
    await supabase.from('call_logs').insert([{ user_id: userId ? Number(userId) : null, message, status }]);
    setUserId('');
    setMessage('');
    setStatus('');
    setIsCreateOpen(false);
    await getLogs();
  };

  const editLog = (log) => {
    setId(log.id);
    setEditUserId(log.user_id || '');
    setEditMessage(log.message || '');
    setEditStatus(log.status || '');
    setIsEditOpen(true);
  };

  const updateLog = async (e) => {
    e.preventDefault();
    await supabase
      .from('call_logs')
      .update({ user_id: editUserId ? Number(editUserId) : null, message: editMessage, status: editStatus })
      .eq('id', id);
    setIsEditOpen(false);
    await getLogs();
  };

  const deleteLog = async (logId) => {
    await supabase.from('call_logs').delete().eq('id', logId);
    await getLogs();
  };

  const filteredLogs = logs.filter((log) => {
    const value = search.toLowerCase();
    const user = Array.isArray(log.users) ? log.users[0] : log.users;
    return log.id.toString().includes(value) || log.message?.toLowerCase().includes(value) || log.status?.toLowerCase().includes(value) || user?.name?.toLowerCase().includes(value);
  });

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / CALL_LOGS_PER_PAGE));
  const startIndex = (currentPage - 1) * CALL_LOGS_PER_PAGE;
  const paginatedLogs = filteredLogs.slice(startIndex, startIndex + CALL_LOGS_PER_PAGE);

  return (
    <Layout>
      <div className="table_page">
        <Title subtitle="Manage call logs" title="Call Logs" />
        <div className="table_controls_card">
          <label className="table_search">
            <img alt="" src={searchIcon} />
            <input onChange={(e) => setSearch(e.target.value)} placeholder="Search call logs..." value={search} />
          </label>
          <div className="table_controls_actions">
            <span>{filteredLogs.length} total records</span>
            <button className="table_primary_button" onClick={() => setIsCreateOpen(true)} type="button">
              Add Call Log
            </button>
          </div>
        </div>
        <div className="table_card">
          <div className="table_scroll">
            <table className="data_table">
              <thead><tr><th>ID</th><th>User</th><th>Message</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {paginatedLogs.map((log) => {
                  const user = Array.isArray(log.users) ? log.users[0] : log.users;
                  return (
                    <tr key={log.id}>
                      <td>{log.id}</td>
                      <td>{user?.name}</td>
                      <td>{log.message}</td>
                      <td>{log.status}</td>
                      <td>
                        <div className="table_actions">
                          <button
                            aria-label={`Edit call log #${log.id}`}
                            className="table_icon_button"
                            onClick={() => editLog(log)}
                            type="button"
                          >
                            <img alt="" src={editIcon} />
                          </button>
                          <button
                            aria-label={`Delete call log #${log.id}`}
                            className="table_icon_button table_icon_button_danger"
                            onClick={() => deleteLog(log.id)}
                            type="button"
                          >
                            <img alt="" src={deleteIcon} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="table_pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} type="button">Previous</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} type="button">Next</button>
          </div>
        </div>
        {isCreateOpen && (
          <div className="table_modal_overlay" onClick={() => setIsCreateOpen(false)}>
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2>Add Call Log</h2>
                </div>
                <button className="table_modal_close" onClick={() => setIsCreateOpen(false)} type="button">
                  x
                </button>
              </div>
              <form className="table_form" onSubmit={createLog}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>User</span>
                    <select onChange={(e) => setUserId(e.target.value)} value={userId}>
                      <option value="">Select user</option>
                      {users.map((user) => <option key={user.id} value={user.id}>{user.name || user.email}</option>)}
                    </select>
                  </label>
                  <label className="table_form_field">
                    <span>Status</span>
                    <input onChange={(e) => setStatus(e.target.value)} placeholder="Status" value={status} />
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Message</span>
                    <textarea onChange={(e) => setMessage(e.target.value)} rows={3} value={message} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsCreateOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}
        {isEditOpen && (
          <div className="table_modal_overlay" onClick={() => setIsEditOpen(false)}>
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2>Edit Call Log</h2>
                </div>
                <button className="table_modal_close" onClick={() => setIsEditOpen(false)} type="button">
                  x
                </button>
              </div>
              <form className="table_form" onSubmit={updateLog}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>User</span>
                    <select onChange={(e) => setEditUserId(e.target.value)} value={editUserId}>
                      <option value="">Select user</option>
                      {users.map((user) => <option key={user.id} value={user.id}>{user.name || user.email}</option>)}
                    </select>
                  </label>
                  <label className="table_form_field">
                    <span>Status</span>
                    <input onChange={(e) => setEditStatus(e.target.value)} value={editStatus} />
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Message</span>
                    <textarea onChange={(e) => setEditMessage(e.target.value)} rows={3} value={editMessage} />
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsEditOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CallLogs;
